import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Heart, BookOpen, Mail, ArrowRight, CheckCircle2, Layers, HandHeart, ScrollText } from "lucide-react";

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    const m = params.get("magic");
    if (m === "invalid") toast.error("That link is no longer valid. Send a fresh one below.");
    if (m === "expired") toast.error("That link expired. Send a fresh one below.");
    const g = params.get("google");
    if (g) toast.error("Google sign-in couldn't complete. Please try again.");
  }, [params]);

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Uses our own backend Google OAuth flow so the consent screen is branded as "My Daily Prayer".
    window.location.href = `${window.location.origin}/api/auth/google/start`;
  };

  const handleMagicSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setSending(true);
    try {
      await api.post("/auth/magic/request", { email: trimmed });
      setSent(true);
    } catch (err) {
      toast.error("Couldn't send the link. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grain-bg min-h-screen w-full relative overflow-hidden" data-testid="landing-page">
      <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, #D4C7E1 0%, transparent 60%)" }} />
      <div className="absolute -bottom-32 -right-16 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, #EFEBF4 0%, transparent 60%)" }} />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="brand-mark">
            <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: "#9B8BAF" }}>
              <Heart className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-lg tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>My Daily Prayer</span>
          </div>
          <div className="hidden sm:flex items-center gap-4">
            <Link
              to="/support"
              data-testid="landing-support-link"
              className="text-xs uppercase tracking-[0.18em] text-[#75737A] hover:text-[#2C2A2E] transition-colors"
            >
              Support
            </Link>
            <Link
              to="/contact"
              data-testid="landing-contact-link"
              className="text-xs uppercase tracking-[0.18em] text-[#75737A] hover:text-[#2C2A2E] transition-colors"
            >
              Contact
            </Link>
            <div className="flex items-center gap-1 text-xs text-[#75737A] uppercase tracking-[0.2em]">
              <Sparkles className="h-3 w-3" /> A quiet place with God
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] bg-[#EFEBF4] text-[#75737A] border border-[#D4C7E1]">
              <BookOpen className="h-3 w-3" /> Prayer &amp; Scripture Journal
            </div>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#2C2A2E]" data-testid="hero-title">
              Bring every prayer
              <br />
              before God, <span className="italic text-[#9B8BAF]">confidently.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-[#75737A]">
              A quiet, private place to bring your prayers before the Lord.
              Keep track of the people and needs on your heart, return to them
              in faith, and remember God's answered prayers with gratitude.
            </p>
            <p className="mt-4 max-w-lg text-sm text-[#9B8BAF] italic" style={{ fontFamily: "'Playfair Display', serif" }}>
              &ldquo;Be constant in prayer.&rdquo; <span className="not-italic text-[#75737A] text-xs tracking-wide">— Romans 12:12</span>
            </p>

            {/* Auth card */}
            <div className="mt-10 max-w-md space-y-5" data-testid="auth-card">
              <Button
                data-testid="google-signin-btn"
                onClick={handleGoogleSignIn}
                className="btn-primary rounded-full w-full h-12 text-sm tracking-wide"
              >
                <GoogleGlyph /> Continue with Google
              </Button>

              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-[#75737A]">
                <div className="h-px flex-1 bg-[#D4C7E1]" />
                <span>Or sign in with email</span>
                <div className="h-px flex-1 bg-[#D4C7E1]" />
              </div>

              {sent ? (
                <div
                  className="rounded-2xl border border-[#D4C7E1] bg-white p-6"
                  data-testid="magic-sent-state"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-full bg-[#EFEBF4] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-[#9B8BAF]" />
                    </div>
                    <div>
                      <h3 className="text-lg text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                        Check your inbox
                      </h3>
                      <p className="mt-1 text-sm text-[#75737A] leading-relaxed">
                        We sent a sign-in link to <b className="text-[#2C2A2E]">{email}</b>. It expires in 15 minutes.
                      </p>
                      <button
                        type="button"
                        onClick={() => { setSent(false); setEmail(""); }}
                        className="mt-3 text-xs text-[#9B8BAF] hover:text-[#8A799E] underline underline-offset-4"
                        data-testid="magic-use-different-email"
                      >
                        Use a different email
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleMagicSubmit} className="space-y-3" data-testid="magic-form">
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#75737A]" />
                    <Input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className="pl-11 h-12 rounded-full bg-white border-[#D4C7E1]"
                      data-testid="magic-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending || !email.trim()}
                    variant="outline"
                    className="w-full h-12 rounded-full border-[#D4C7E1] text-[#2C2A2E] hover:bg-[#EFEBF4]"
                    data-testid="magic-submit-btn"
                  >
                    {sending ? "Sending…" : (<><span>Send me a sign-in link</span><ArrowRight className="h-4 w-4 ml-1.5" /></>)}
                  </Button>
                </form>
              )}
              <p className="text-xs text-[#75737A]/90 leading-relaxed">
                Secure sign-in. A private space to pray, reflect, and remember God's faithfulness. No passwords.
              </p>
              <p className="text-sm text-[#9B8BAF] italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                &ldquo;Cast all your anxiety on Him because He cares for you.&rdquo;
                <span className="not-italic text-[#75737A] text-xs tracking-wide"> — 1 Peter 5:7</span>
              </p>
            </div>

            <div className="mt-12 flex items-center gap-6 text-xs text-[#75737A]">
              <div>
                <div className="text-2xl text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>01</div>
                <div className="uppercase tracking-[0.18em] mt-1">Pray</div>
              </div>
              <div className="h-8 w-px bg-[#D4C7E1]" />
              <div>
                <div className="text-2xl text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>02</div>
                <div className="uppercase tracking-[0.18em] mt-1">Trust</div>
              </div>
              <div className="h-8 w-px bg-[#D4C7E1]" />
              <div>
                <div className="text-2xl text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>03</div>
                <div className="uppercase tracking-[0.18em] mt-1">Remember</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl blur-2xl opacity-60" style={{ background: "linear-gradient(135deg, #EFEBF4, #FAF9F6)" }} />
              <div className="relative bg-white rounded-2xl border border-[#D4C7E1] p-8 shadow-[0_20px_60px_-30px_rgba(90,70,120,0.35)]">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#75737A]">Example</span>
                  <span className="text-xs text-[#9B8BAF] uppercase tracking-[0.18em]">Preview</span>
                </div>
                <h3 className="mt-4 text-2xl text-[#2C2A2E]">For Grandma's healing</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#75737A]">
                  Praying for strength, comfort, and healing this week. May the Lord surround her with peace, renew her body, and remind her that He is near.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="chip">Family</span>
                  <span className="chip">Health</span>
                </div>
                <div className="mt-6 pt-5 border-t border-[#EFEBF4] flex items-center justify-between text-xs text-[#75737A]">
                  <span>Prayed 4 times</span>
                  <span className="text-[#92A796]">↺ In prayer</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What you get inside — feature strip */}
        <section className="mt-28 md:mt-36" data-testid="features-section">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#75737A]">A quiet place to pray</div>
            <h2 className="mt-3 text-4xl sm:text-5xl leading-tight text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              What you'll find <span className="italic text-[#9B8BAF]">inside.</span>
            </h2>
            <p className="mt-4 text-[#75737A] text-lg leading-relaxed">
              A calm, private journal that grows with you — one prayer at a time.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6 md:gap-7">
            <FeatureCard
              icon={Layers}
              title="Keep your prayers organized"
              body="Create prayers for family, health, work, relationships, or anything on your heart. Tag and filter them however you'd like."
              testId="feature-organize"
            />
            <FeatureCard
              icon={HandHeart}
              title="Return to them daily"
              body="Tap ‘Prayed today' and see how faithfully you've continued praying — a gentle reminder that you keep showing up."
              testId="feature-return"
            />
            <FeatureCard
              icon={CheckCircle2}
              title="Remember answered prayers"
              body="Move prayers into an Answered section and celebrate what the Lord has done. A quiet log of His faithfulness."
              testId="feature-answered"
              accent="#8FA396"
            />
            <FeatureCard
              icon={ScrollText}
              title="Reflect with Scripture"
              body="Keep verses alongside what's on your heart. A prayer journal and a Scripture journal, gently woven together."
              testId="feature-scripture"
            />
          </div>
        </section>

        {/* Privacy pitch */}
        <section className="mt-24 md:mt-32 rounded-3xl border border-[#D4C7E1] bg-white/70 backdrop-blur px-8 py-10 md:px-12 md:py-14" data-testid="privacy-section">
          <div className="grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#75737A]">Your prayers are private. Period.</div>
              <h2 className="mt-3 text-3xl sm:text-4xl leading-tight text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Your prayer journal <span className="italic text-[#9B8BAF]">belongs to you.</span>
              </h2>
              <p className="mt-4 text-[#75737A] leading-relaxed">
                We don't sell your data, show ads, share your prayers, or use them to train AI. Ever.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to="/privacy"
                data-testid="landing-privacy-cta"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#D4C7E1] px-5 py-2.5 text-sm text-[#2C2A2E] hover:bg-[#EFEBF4] transition-colors"
              >
                Learn about privacy <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Footer with legal links (visible for Google OAuth verification) */}
        <footer className="mt-24 pt-8 border-t border-[#EFEBF4] flex flex-wrap gap-x-6 gap-y-2 items-center justify-center text-xs text-[#75737A]">
          <span>&copy; My Daily Prayer</span>
          <Link to="/privacy" className="hover:text-[#2C2A2E]" data-testid="landing-privacy-link">Privacy</Link>
          <Link to="/terms" className="hover:text-[#2C2A2E]" data-testid="landing-terms-link">Terms</Link>
          <Link to="/support" className="hover:text-[#2C2A2E]">Support</Link>
          <Link to="/contact" className="hover:text-[#2C2A2E]">Contact</Link>
        </footer>
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body, testId, accent = "#9B8BAF" }) {
  return (
    <div
      className="prayer-card p-7 md:p-8"
      data-testid={testId}
    >
      <div
        className="h-10 w-10 rounded-full flex items-center justify-center"
        style={{ background: "#EFEBF4" }}
      >
        <Icon className="h-4 w-4" style={{ color: accent }} strokeWidth={2.5} />
      </div>
      <h3
        className="mt-5 text-xl text-[#2C2A2E] leading-snug"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {title}
      </h3>
      <p className="mt-3 text-[15px] leading-relaxed text-[#75737A]">{body}</p>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#FFFFFF" d="M21.35 11.1H12v3.2h5.35c-.23 1.2-1.44 3.53-5.35 3.53-3.22 0-5.85-2.66-5.85-5.94s2.63-5.94 5.85-5.94c1.83 0 3.06.78 3.76 1.44l2.56-2.47C16.62 3.63 14.55 2.75 12 2.75 6.98 2.75 2.9 6.83 2.9 11.85s4.08 9.1 9.1 9.1c5.25 0 8.72-3.68 8.72-8.87 0-.6-.06-1.05-.14-1.5z"/>
    </svg>
  );
}
